import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./ErpDashboard.css";
import BossDashboardTabs from "./BossDashboardTabs";

function formatMoney(value) {
  return `${Number(value || 0).toLocaleString()} Ks`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

export default function ErpDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadSummary() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_BASE}/api/erp/dashboard-summary`);
      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load ERP dashboard");
      }

      setSummary(data);
    } catch (err) {
      setError(err.message || "Dashboard loading failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSummary();
  }, []);

  const total = summary?.total || {};
  const shops = summary?.shops || [];

  const topShop = useMemo(() => {
    if (!shops.length) return null;

    return [...shops].sort(
      (a, b) =>
        Number(b.salesTodayAmount || 0) - Number(a.salesTodayAmount || 0)
    )[0];
  }, [shops]);

  return (
    <Layout title="ERP Dashboard">
      <div className="erpDash">
        <div className="erpDash__hero">
          <div>
            <div className="erpDash__eyebrow">Retail ERP System</div>
            <h1>Clean Office Dashboard</h1>
            <p>
              Sales, inventory, coupons, waybill and shop operation overview.
            </p>
          </div>

          <div className="erpDash__actions">
            <button onClick={loadSummary} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <span>Read-only Mode</span>
          </div>
        </div>

        {error && <div className="erpDash__error">{error}</div>}
<BossDashboardTabs />

      <div className="erpDash__meta">
          <div>
            <span>Date</span>
            <strong>{summary?.date || "-"}</strong>
          </div>

          <div>
            <span>Shops</span>
            <strong>{formatNumber(summary?.shopCount)}</strong>
          </div>

          <div>
            <span>Server Time</span>
            <strong>
              {summary?.serverTime
                ? new Date(summary.serverTime).toLocaleString()
                : "-"}
            </strong>
          </div>
        </div>

        <div className="erpDash__cards">
          <div className="erpDashCard erpDashCard--primary">
            <span>Today Sales</span>
            <strong>{formatMoney(total.salesTodayAmount)}</strong>
            <small>{formatNumber(total.salesTodayCount)} vouchers today</small>
          </div>

          <div className="erpDashCard">
            <span>All Sales</span>
            <strong>{formatNumber(total.allSalesCount)}</strong>
            <small>Total saved vouchers</small>
          </div>

          <div className="erpDashCard">
            <span>Product Codes</span>
            <strong>{formatNumber(total.productCodeCount)}</strong>
            <small>Across all shops</small>
          </div>

          <div className="erpDashCard erpDashCard--warning">
            <span>Waybill Pending</span>
            <strong>{formatNumber(total.waybillPendingCount)}</strong>
            <small>Need shop action</small>
          </div>

          <div className="erpDashCard">
            <span>Coupons</span>
            <strong>{formatNumber(summary?.couponCount)}</strong>
            <small>Gift vouchers and coupons</small>
          </div>

          <div
            className="erpDashCard erpDashCard--memberLink"
            role="button"
            tabIndex={0}
            onClick={() => {
              window.location.href = "/erp/members";
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                window.location.href = "/erp/members";
              }
            }}
          >
            <span>Members</span>
            <strong>Open</strong>
            <small>Member list, QR, Excel import & purchase history</small>
          </div>

          <div className="erpDashCard erpDashCard--soft">
            <span>Top Shop Today</span>
            <strong>{topShop?.shop || "-"}</strong>
            <small>
              {topShop ? formatMoney(topShop.salesTodayAmount) : "No data"}
            </small>
          </div>
        </div>

        <div className="erpDash__grid">
          <section className="erpDashPanel erpDashPanel--large">
            <div className="erpDashPanel__head">
              <div>
                <h3>Shop Performance</h3>
                <p>Today sales and operation summary by shop.</p>
              </div>
            </div>

            <div className="erpDashTableWrap">
              <table className="erpDashTable">
                <thead>
                  <tr>
                    <th>Shop</th>
                    <th>Today Vouchers</th>
                    <th>Today Sales</th>
                    <th>All Sales</th>
                    <th>Products</th>
                    <th>Waybill</th>
                  </tr>
                </thead>

                <tbody>
                  {shops.map((row) => (
                    <tr key={row.shop}>
                      <td>
                        <span className="shopBadge">{row.shop}</span>
                      </td>
                      <td>{formatNumber(row.salesTodayCount)}</td>
                      <td>{formatMoney(row.salesTodayAmount)}</td>
                      <td>{formatNumber(row.allSalesCount)}</td>
                      <td>{formatNumber(row.productCodeCount)}</td>
                      <td>
                        <span
                          className={
                            row.waybillPendingCount > 0
                              ? "statusBadge statusBadge--warning"
                              : "statusBadge statusBadge--ok"
                          }
                        >
                          {formatNumber(row.waybillPendingCount)}
                        </span>
                      </td>
                    </tr>
                  ))}

                  {!loading && shops.length === 0 && (
                    <tr>
                      <td colSpan="6">No shop summary found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="erpDashPanel">
            <h3>System Status</h3>

            <div className="statusList">
              <div>
                <span>Backend API</span>
                <strong className={summary?.success ? "okText" : "badText"}>
                  {summary?.success ? "Connected" : "Disconnected"}
                </strong>
              </div>

              <div>
                <span>Mode</span>
                <strong>{summary?.mode || "read-only"}</strong>
              </div>

              <div>
                <span>Module</span>
                <strong>{summary?.module || "ERP Dashboard"}</strong>
              </div>

              <div>
                <span>Theme</span>
                <strong>White / Grey / Soft Blue</strong>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}







